/**
 * @file Builds the email footer HTML for an organization.
 */

import { getConfig } from '@components/Config';
import { formatPhoneNumber } from '@components/Text';

import getPortalUrl from './getPortalUrl';

type LinkTemplates = {
  portalUrl?: string;
};

type OrganizationFooterData = {
  name?: string;
  email?: string;
  domain?: string;
  phoneNumber?: string;
  linkTemplates?: LinkTemplates;
  isEmailVerified?: boolean;
  emailFooterSource?: string;
};

const PLATFORM_NAME = getConfig('PLATFORM_NAME') as string;

/**
 * Builds the signature block HTML with the organization's name, email, phone number and portal link.
 */
const getOrganizationFooterHtml = (
  organization: OrganizationFooterData = {},
): string => {
  const {
    name = PLATFORM_NAME,
    email,
    phoneNumber,
  } = organization;

  const rows: string[] = [
    'Regards,',
    name,
  ];

  const hasEmail = Boolean(email);

  if (hasEmail) {
    rows.push(`Email: <a href="mailto:${email}">${email}</a>`);
  }

  const hasPhoneNumber = Boolean(phoneNumber);

  if (hasPhoneNumber) {
    const formattedPhoneNumber = formatPhoneNumber(phoneNumber) || phoneNumber;
    rows.push(
      `Phone: <a href="tel:${phoneNumber}">${formattedPhoneNumber}</a>`,
    );
  }

  const portalUrl = getPortalUrl(organization);
  const hasPortalUrl = Boolean(portalUrl);

  if (hasPortalUrl) {
    rows.push(`Portal Login: <a href="${portalUrl}">${portalUrl}</a>`);
  }

  const html = rows
    .join('<br/>');

  return html;
};

export default getOrganizationFooterHtml;
